import React from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { setModalStatus } from "../../reducers/Viewer";
import { useLocation } from "react-router";
import { useParams } from "react-router-dom";
import { RootState } from "@Reducers/index";
import { selectionStages } from "@Actions/Viewer";
import { BIM_ENGINE, CAD_ENGINE } from "@Services/constants";
import type { ModalEntry } from "@Reducers/Viewer";

/**
 * The two fields this hook reads off its argument. The parameter itself stays `object`: the
 * design-viewer layout-settings panel calls the hook with its OWN, unrelated props object, and a
 * named all-optional parameter type would fail the weak-type check there.
 */
type ModulesMapperInput = { estimateId?: unknown; fileVersionId?: string };

const useModulesMapper = (props: object = {}) => {
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const fileVersionId = (props as ModulesMapperInput).fileVersionId;
  const params = useParams();
  const filesData = useSelector((state: RootState) => state.filesData);
  const dispatch = useDispatch();
  const fileType = filesData[fileVersionId!]?.engineType;

  const handleToolClick = (e: React.MouseEvent<HTMLElement>) => {
    // The clicked tool id is carried on the DOM `type` attribute. Every sidebar entry renders it
    // (see `getDefaultSidebarValues`), so a missing one means the handler was wired to some other
    // element — nothing to toggle.
    const clickedTool = e.currentTarget.attributes.getNamedItem("type")?.value;
    if (!clickedTool) {
      return;
    }
    const clickedValue = !HoopsModalStatus[clickedTool]?.show;
    let initializeState: ModalEntry = {};

    if (clickedValue) {
      initializeState = modulesMapper[clickedTool]?.initialState || {};
    }

    const clickedObj = {
      [clickedTool]: {
        show: clickedValue,
        ...initializeState,
      },
    };
    const hoopsStatuses = {
      ...HoopsModalStatus,
      ...clickedObj,
    };
    dispatch(setModalStatus(hoopsStatuses));
  };

  const getDefaultSidebarValues = ({ type, isVisible = true }: { type: string; isVisible?: boolean }) => ({
    isVisible,
    isEnabled: true,
    showGradient: HoopsModalStatus[type]?.show,
    onClick: handleToolClick,
  });

  // UNTYPED BY NECESSITY: three call sites in `features/design-viewer` (outside this module) index
  // this dictionary without a presence guard and assert the hook result to a one-element tuple, both
  // of which only compile while the mapper — and hence the returned array — stays `any`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- design-viewer call sites index this map unguarded and assert a one-element tuple
  const modulesMapper: any = {
    ObjectTree: {
      type: "ObjectTree",
      toolTipContent: "Object Tree",
      belongToGroup: "Others",
      name: "Object Tree",
      ...getDefaultSidebarValues({ type: "ObjectTree" }),
    },
    ObjectTreeClone: {
      type: "ObjectTreeClone",
      toolTipContent: "Object Tree",
      belongToGroup: "Others",
      name: "Object Tree Clone",
      ...getDefaultSidebarValues({ type: "ObjectTreeClone" }),
    },
    Layers: {
      type: "Layers",
      toolTipContent: "Layers",
      belongToGroup: "Others",
      name: "Layers",
      ...getDefaultSidebarValues({ type: "Layers" }),
    },
    Types: {
      type: "Types",
      toolTipContent: "Types",
      belongToGroup: "Others",
      name: "Types",
      ...getDefaultSidebarValues({ type: "Types", isVisible: fileType == BIM_ENGINE }),
    },
    Metadata: {
      type: "Metadata",
      ...getDefaultSidebarValues({ type: "Metadata" }),
      toolTipContent: "Metadata",
      belongToGroup: "Others",
      name: "Metadata",
    },
    OldMetadata: {
      type: "OldMetadata",
      ...getDefaultSidebarValues({ type: "OldMetadata" }),
      toolTipContent: "OldMetadata",
      belongToGroup: "Others",
      name: "OldMetadata",
    },
    // AssetData: {
    //   type: "AssetData",
    //   toolTipContent: "Asset Data",
    //   belongToGroup: "Others",
    //   name: "Asset Data",
    //   ...getDefaultSidebarValues({
    //     type: "AssetData",
    //     isVisible: !["prod"].includes(process.env.REACT_APP_ENV || ""),
    //   }),
    // },
    CustomMetadata: {
      type: "CustomMetadata",
      toolTipContent: "Custom Metadata",
      belongToGroup: "Others",
      name: "Custom Metadata",
      ...getDefaultSidebarValues({
        type: "CustomMetadata",
        isVisible: ([BIM_ENGINE, CAD_ENGINE] as (string | undefined)[]).includes(fileType),
      }),
    },
    // Assets: {
    //   type: "Assets",
    //   toolTipContent: "Assets",
    //   belongToGroup: "Assets & Maintenance",
    //   name: "Assets",
    //   ...getDefaultSidebarValues({
    //     type: "Assets",
    //     isVisible: !["prod"].includes(process.env.REACT_APP_ENV || ""),
    //   }),
    // },
    Schedules: {
      type: "Schedules",
      toolTipContent: "Schedule",
      belongToGroup: "Scheduling",
      name: "Schedule",
      ...getDefaultSidebarValues({
        type: "Schedules",
        isVisible: !!urlSearchParams.get("scheduleId"),
      }),
    },
    Resources: {
      type: "Resources",
      toolTipContent: "Resources",
      belongToGroup: "Scheduling",
      name: "Resources",
      ...getDefaultSidebarValues({
        type: "Resources",
        isVisible: !!urlSearchParams.get("scheduleId"),
      }),
    },
    Views: {
      type: "Views",
      toolTipContent: "Views",
      belongToGroup: "Others",
      name: "Views",
      ...getDefaultSidebarValues({ type: "Views" }),
    },
    "Clash Tests": {
      type: "Clash Tests",
      toolTipContent: "Clash Tests",
      belongToGroup: "Design Management",
      name: "Clash Tests",
      currentStage: "Clash Tests",
      backStage: "Clash Tests",
      initialState: {
        currentStage: "Clash Tests",
        backStage: "Clash Tests",
        defaultStage: "Clash Tests",
      },
      ...getDefaultSidebarValues({
        type: "Clash Tests",
        isVisible: filesData[params.fileId!]?.resourceType == "RESOURCE_TYPE_MERGED_FILE",
      }),
    },
    Sheets: {
      type: "Sheets",
      toolTipContent: "Sheets",
      belongToGroup: "Others",
      name: "Sheets",
      ...getDefaultSidebarValues({ type: "Sheets" }),
    },
    ConstituentFiles: {
      type: "ConstituentFiles",
      toolTipContent: "Model Repositioning",
      belongToGroup: "Others",
      name: "ModelReposition",
      ...getDefaultSidebarValues({ type: "ConstituentFiles", isVisible: fileType == BIM_ENGINE }),
      initialState: {
        currentStage: "constituentFiles",
        defaultStage: "constituentFiles",
        backStage: "constituentFiles",
      },
    },
    Relationship: {
      type: "Relationship",
      toolTipContent: "Relationship",
      belongToGroup: "Others",
      name: "Relationship",
      ...getDefaultSidebarValues({ type: "Relationship", isVisible: fileType == BIM_ENGINE }),
    },
    Divider: {
      type: "Divider",
      name: "Divider",
      isVisible: true,
    },

    Properties: {
      type: "Properties",
      toolTipContent: "Properties",
      belongToGroup: "Others",
      name: "Properties",
      ...getDefaultSidebarValues({ type: "Properties", isVisible: true }),
    },
    Issues: {
      type: "Issues",
      toolTipContent: "Issues",
      belongToGroup: "Design Management",
      name: "Issues",
      ...getDefaultSidebarValues({ type: "Issues", isVisible: !urlSearchParams.get("transmittalId") }),
      initialState: {
        currentStage: "issues",
        defaultStage: "issues",
        backStage: "issues",
        stageMapper: {
          issues: { title: "Issues" },
          create_issue: { title: "Create Issue" },
          edit_issue: { title: "Edit Issue" },
          view_issue: { title: "View Issue" },
        },
      },
    },
    /* Attachments: {
      type: "Attachments",
      toolTipContent: "Attachments",
      belongToGroup: "Assets & Maintenance",
      name: "Attachments",
      ...getDefaultSidebarValues({ type: "Attachments", isVisible: false }),
    }, */
    Queries: {
      type: "Queries",
      toolTipContent: "Queries",
      belongToGroup: "Others",
      name: "Queries",
      ...getDefaultSidebarValues({ type: "Queries", isVisible: fileType == BIM_ENGINE }),
      initialState: {
        currentStage: "queries_list",
        defaultStage: "queries_list",
        backStage: "queries_list",
      },
    },
    SearchSet: {
      type: "SearchSet",
      toolTipContent: "Data Bank",
      belongToGroup: "Others",
      name: "DataBankIcon",
      ...getDefaultSidebarValues({
        type: "SearchSet",
        isVisible: fileType == BIM_ENGINE,
      }),
    },
    // SearchSet1: {
    //   type: 'SearchSet1',
    //   belongToGroup: 'Others',
    //   name: 'Data Bank  Material UI',
    //   ...getDefaultSidebarValues({ type: 'SearchSet1', isVisible: !!props.estimateId }),
    // },
    Measurements: {
      type: "Measurements",
      toolTipContent: "Measurements",
      belongToGroup: "Estimation",
      name: "Measurement",
      ...getDefaultSidebarValues({
        type: "Measurements",
        isVisible: !!(props as ModulesMapperInput).estimateId,
      }),
    },
    Takeoffs: {
      type: "Takeoffs",
      toolTipContent: "Takeoffs",
      belongToGroup: "Estimation",
      name: "Takeoffs",
      initialState: {
        currentStage: "Takeoffs",
        backStage: "Takeoffs",
        defaultStage: "Takeoffs",
      },
      ...getDefaultSidebarValues({
        type: "Takeoffs",
        isVisible: !!(props as ModulesMapperInput).estimateId,
      }),
    },
    TakeoffWizard: {
      type: "TakeoffWizard",
      toolTipContent: "Takeoff Wizard",
      belongToGroup: "Estimation",
      name: "Takeoff Wizard",
      ...getDefaultSidebarValues({
        type: "TakeoffWizard",
        isVisible: !!(props as ModulesMapperInput).estimateId && fileType == BIM_ENGINE,
      }),
    },
    SelectionSet: {
      type: "SelectionSet",
      toolTipContent: "Selection Set",
      belongToGroup: "Others",
      name: "Selection Set",
      ...getDefaultSidebarValues({ type: "SelectionSet", isVisible: fileType == BIM_ENGINE }),
      initialState: {
        currentStage: selectionStages.SELECTION_SET_LIST,
        defaultStage: selectionStages.SELECTION_SET_LIST,
        backStage: selectionStages.SELECTION_SET_LIST,
      },
    },
    // Temperature: {
    //   type: "Temperature",
    //   toolTipContent: "IoT Data",
    //   belongToGroup: "Others",
    //   name: "IoT Data",
    //   ...getDefaultSidebarValues({
    //     type: "Temperature",
    //     isVisible: !["prod"].includes(process.env.REACT_APP_ENV || ""),
    //   }),
    // },
    // Collaboration: {
    //   type: "Collaboration",
    //   toolTipContent: "Collaboration",
    //   belongToGroup: "Others",
    //   name: "Collaboration",
    //   ...getDefaultSidebarValues({
    //     type: "Collaboration",
    //     isVisible: !["prod"].includes(process.env.REACT_APP_ENV || ""),
    //   }),
    // },
  };

  return [modulesMapper, handleToolClick];
};

export default useModulesMapper;
